import {
  BUSINESS_REPORT_DEFINITIONS,
  EBusinessReportKind,
  EBusinessReportModuleStatus,
  EReportingDomain,
  businessReportInputSchema,
} from "./businessReporting"
import type { BusinessReportInput, BusinessReportModule } from "./businessReporting"

export const ALL_BUSINESS_REPORT_PRODUCTS = "all" as const
const MAX_EXECUTIVE_FINDINGS = 3

export interface BusinessReportFinding {
  id: string
  domain: EReportingDomain
  title: string
  observation: string
  proposedAction: string
  productKeys: string[]
  evidence: {
    requestedWindow: { start: string; end: string }
    actualWindow: { start: string; end: string } | null
    sourceIds: string[]
    collectionIds: string[]
  }
  priority: number
}

export interface BusinessReportModuleTable {
  title: string
  columns: string[]
  rows: string[][]
}

function metric(value: number | null, unavailable = "Unknown"): string {
  return value === null ? unavailable : String(value)
}

function rate(value: number | null, unavailable = "Unknown"): string {
  return value === null ? unavailable : `${(value * 100).toFixed(1)}%`
}

/** Shared presentation vocabulary for the private web and portable HTML editions. */
export function businessReportModuleTables(module: BusinessReportModule): BusinessReportModuleTable[] {
  if (!module.data) return []
  if (module.domain === EReportingDomain.SEARCH)
    return [
      {
        title: "Organic search",
        columns: ["Product", "Clicks", "Impressions", "Source days"],
        rows: module.data.products.map((row) => [
          row.productKey,
          metric(row.clicks, "Unavailable"),
          metric(row.impressions, "Unavailable"),
          `${row.observedDays}/${row.expectedDays}`,
        ]),
      },
    ]
  if (module.domain === EReportingDomain.MARKETING)
    return [
      {
        title: "Paid acquisition",
        columns: [
          "Product",
          "Provider account",
          "Environment",
          "Mode",
          "Currency",
          "Spend minor",
          "Conversions",
          "Attribution",
          "Reconciliation",
        ],
        rows: module.data.products.map((row) => [
          row.productKey,
          `${row.provider}:${row.providerAccountId}`,
          row.environment,
          row.livemode ? "live" : "test",
          row.currency.toUpperCase(),
          metric(row.spendMinor, "Unavailable"),
          metric(row.providerConversions, "Unavailable"),
          row.attributionModel,
          row.reconciliationStatus,
        ]),
      },
    ]
  if (module.domain === EReportingDomain.BILLING)
    return [
      {
        title: "Revenue cohorts",
        columns: [
          "Product",
          "Provider account",
          "Environment",
          "Mode",
          "Cohort",
          "Currency",
          "Subjects",
          "Converted",
          "Conversion",
          "Net revenue minor",
          "State",
        ],
        rows: module.data.cohorts.map((row) => [
          row.productKey,
          `${row.provider}:${row.stripeAccountKey}`,
          row.environment,
          row.livemode ? "live" : "test",
          row.cohort,
          row.currency.toUpperCase(),
          String(row.subjects),
          String(row.convertedSubjects),
          rate(row.conversionRate),
          metric(row.netRevenueMinor),
          row.state,
        ]),
      },
      {
        title: "Retention",
        columns: [
          "Product",
          "Provider account",
          "Environment",
          "Cohort",
          "Horizon",
          "Eligible",
          "Retained",
          "Unknown",
          "Retention",
          "Maturity",
        ],
        rows: module.data.retention.map((row) => [
          row.productKey,
          `${row.provider}:${row.stripeAccountKey}`,
          row.environment,
          row.cohort,
          `D${row.horizonDays}`,
          String(row.eligibleSubjects),
          String(row.retainedSubjects),
          String(row.unknownSubjects),
          rate(row.retentionRate, row.matured ? "Unknown" : "Immature"),
          row.matured ? row.state : "immature",
        ]),
      },
    ]
  if (module.domain === EReportingDomain.ENGINEERING)
    return [
      {
        title: "Engineering delivery",
        columns: [
          "Scope",
          "Repositories",
          "Merged PRs",
          "Ready to merge hours",
          "Review coverage",
          "CI attempts",
          "CI successes",
          "Deployments",
        ],
        rows: module.data.products.map((row) => [
          row.productKey ?? "Shared company",
          row.repositoryIds.join(", "),
          metric(row.mergedPullRequests, "Unavailable"),
          metric(row.medianReadyToMergeHours, "Unavailable"),
          rate(row.reviewCoverageRate, "Unavailable"),
          metric(row.ciAttempts, "Unavailable"),
          metric(row.ciSuccessfulAttempts, "Unavailable"),
          metric(row.deploymentCount, "Unavailable"),
        ]),
      },
    ]
  return [
    {
      title: "Operations",
      columns: ["Product", "Check exceptions", "Open incidents", "Failed deployments", "Evidence detail"],
      rows: module.data.products.map((row) => [
        row.productKey,
        metric(row.checkExceptions, "Unavailable"),
        metric(row.openIncidents, "Unavailable"),
        metric(row.failedDeployments, "Unavailable"),
        row.detail,
      ]),
    },
  ]
}

function moduleProductKeys(module: BusinessReportModule): string[] {
  if (!module.data) return []
  if (module.domain === EReportingDomain.BILLING)
    return [...new Set([...module.data.cohorts, ...module.data.retention].map((row) => row.productKey))].sort()
  return [
    ...new Set(module.data.products.map((row) => row.productKey).filter((key): key is string => key !== null)),
  ].sort()
}

function filterModule(module: BusinessReportModule, selected: Set<string>): BusinessReportModule {
  if (!module.data) return module
  if (module.domain === EReportingDomain.BILLING)
    return {
      ...module,
      data: {
        cohorts: module.data.cohorts.filter((row) => selected.has(row.productKey)),
        retention: module.data.retention.filter((row) => selected.has(row.productKey)),
      },
    }
  return {
    ...module,
    data: {
      products: module.data.products.filter((row) =>
        module.domain === EReportingDomain.ENGINEERING && row.productKey === null
          ? true
          : selected.has(row.productKey ?? ""),
      ),
    },
  } as BusinessReportModule
}

function evidenceFor(module: BusinessReportModule) {
  return {
    requestedWindow: module.evidence.requestedWindow,
    actualWindow: module.evidence.actualWindow,
    sourceIds: module.evidence.provenance.sourceIds,
    collectionIds: module.evidence.provenance.collectionIds,
  }
}

function gapFinding(module: BusinessReportModule, required: boolean): BusinessReportFinding | null {
  if (module.status === EBusinessReportModuleStatus.AVAILABLE) return null
  const products = moduleProductKeys(module)
  return {
    id: `${module.domain}-coverage-gap`,
    domain: module.domain,
    title: `${module.domain[0]?.toUpperCase()}${module.domain.slice(1)} evidence is ${module.status}`,
    observation: module.reason ?? "The frozen source envelope does not establish complete evidence.",
    proposedAction: required
      ? "Restore and reconcile the required source coverage before treating this edition as complete."
      : "Review the source receipt before using this module for a decision.",
    productKeys: products,
    evidence: evidenceFor(module),
    priority: required ? 0 : 1,
  }
}

function searchFinding(
  module: Extract<BusinessReportModule, { domain: EReportingDomain.SEARCH }>,
): BusinessReportFinding | null {
  if (!module.data) return null
  const incomplete = module.data.products.filter((row) => row.observedDays < row.expectedDays)
  if (!incomplete.length) return null
  return {
    id: "search-incomplete-products",
    domain: module.domain,
    title: "Organic search coverage needs review",
    observation: `${incomplete.length} selected product${incomplete.length === 1 ? " has" : "s have"} incomplete source-day coverage. Missing days do not establish a traffic decline.`,
    proposedAction: "Inspect the frozen collection receipts and restore exact-date coverage before comparing periods.",
    productKeys: incomplete.map((row) => row.productKey).sort(),
    evidence: evidenceFor(module),
    priority: 2,
  }
}

function marketingFinding(
  module: Extract<BusinessReportModule, { domain: EReportingDomain.MARKETING }>,
): BusinessReportFinding | null {
  if (!module.data) return null
  const unreconciled = module.data.products.filter((row) => row.reconciliationStatus !== "reconciled")
  if (!unreconciled.length) return null
  return {
    id: "marketing-unreconciled",
    domain: module.domain,
    title: "Paid acquisition reconciliation needs review",
    observation: `${unreconciled.length} bound account${unreconciled.length === 1 ? " is" : "s are"} not reconciled. Provider-attributed conversions remain distinct from billing customers.`,
    proposedAction:
      "Reconcile the exact provider account and window before using attributed cost as customer acquisition cost.",
    productKeys: [...new Set(unreconciled.map((row) => row.productKey))].sort(),
    evidence: evidenceFor(module),
    priority: 2,
  }
}

function billingFinding(
  module: Extract<BusinessReportModule, { domain: EReportingDomain.BILLING }>,
): BusinessReportFinding | null {
  if (!module.data) return null
  const unknownCohorts = module.data.cohorts.filter((row) => row.state === "unknown")
  const immatureRetention = module.data.retention.filter((row) => !row.matured || row.state === "unknown")
  if (!unknownCohorts.length && !immatureRetention.length) return null
  return {
    id: "billing-unknown-or-immature",
    domain: module.domain,
    title: "Revenue or retention evidence is incomplete",
    observation: `${unknownCohorts.length} cohort rows are unknown and ${immatureRetention.length} retention rows are immature or unknown. Current cohorts are not final horizon outcomes.`,
    proposedAction:
      "Use only reconciled mature rows for decisions and retain null outcomes until their eligibility horizon passes.",
    productKeys: [...new Set([...unknownCohorts, ...immatureRetention].map((row) => row.productKey))].sort(),
    evidence: evidenceFor(module),
    priority: 1,
  }
}

function engineeringFinding(
  module: Extract<BusinessReportModule, { domain: EReportingDomain.ENGINEERING }>,
): BusinessReportFinding | null {
  if (!module.data) return null
  const unknown = module.data.products.filter(
    (row) => row.mergedPullRequests === null || row.ciAttempts === null || row.medianReadyToMergeHours === null,
  )
  if (!unknown.length) return null
  return {
    id: "engineering-history-gap",
    domain: module.domain,
    title: "Engineering history is incomplete",
    observation: `${unknown.length} selected product${unknown.length === 1 ? " lacks" : "s lack"} accepted PR or CI coverage for the requested window.`,
    proposedAction:
      "Verify bound repository ownership and retained source history before interpreting delivery trends.",
    productKeys: unknown
      .map((row) => row.productKey)
      .filter((key): key is string => key !== null)
      .sort(),
    evidence: evidenceFor(module),
    priority: 1,
  }
}

function operationsFinding(
  module: Extract<BusinessReportModule, { domain: EReportingDomain.OPERATIONS }>,
): BusinessReportFinding | null {
  if (!module.data) return null
  const exceptions = module.data.products.filter(
    (row) => (row.checkExceptions ?? 0) > 0 || (row.openIncidents ?? 0) > 0 || (row.failedDeployments ?? 0) > 0,
  )
  if (!exceptions.length) return null
  return {
    id: "operations-exceptions",
    domain: module.domain,
    title: "Operational exceptions need disposition",
    observation: `${exceptions.length} selected product${exceptions.length === 1 ? " has" : "s have"} persisted check, incident, or deployment exceptions. These populations are not deduplicated.`,
    proposedAction:
      "Inspect the source records in their existing incident and deployment workflows before assigning impact.",
    productKeys: exceptions.map((row) => row.productKey).sort(),
    evidence: evidenceFor(module),
    priority: 2,
  }
}

function domainFinding(module: BusinessReportModule): BusinessReportFinding | null {
  switch (module.domain) {
    case EReportingDomain.SEARCH:
      return searchFinding(module)
    case EReportingDomain.MARKETING:
      return marketingFinding(module)
    case EReportingDomain.BILLING:
      return billingFinding(module)
    case EReportingDomain.ENGINEERING:
      return engineeringFinding(module)
    case EReportingDomain.OPERATIONS:
      return operationsFinding(module)
  }
}

export function deriveBusinessReport(input: BusinessReportInput, requestedProductKeys?: string[]) {
  const parsed = businessReportInputSchema.parse(input) as BusinessReportInput
  const selectedProductKeys = requestedProductKeys?.length
    ? [...new Set(requestedProductKeys)].filter((key) => parsed.productKeys.includes(key)).sort()
    : parsed.productKeys
  const selected = new Set(selectedProductKeys)
  const modules = parsed.modules.map((module) => filterModule(module, selected))
  const definition = BUSINESS_REPORT_DEFINITIONS[parsed.reportKind]
  const gaps = modules
    .map((module) => {
      const requirement = definition.modules.find((entry) => entry.domain === module.domain)
      return gapFinding(module, requirement?.required ?? false)
    })
    .filter((finding): finding is BusinessReportFinding => finding !== null)
  const domainFindings = modules
    .map(domainFinding)
    .filter((finding): finding is BusinessReportFinding => finding !== null)
  const findings = [...gaps, ...domainFindings]
    .sort((left, right) => left.priority - right.priority || left.id.localeCompare(right.id))
    .slice(0, parsed.reportKind === EBusinessReportKind.EXECUTIVE ? MAX_EXECUTIVE_FINDINGS : 100)
  const requiredDomains = new Set(definition.modules.filter((entry) => entry.required).map((entry) => entry.domain))
  const requiredComplete = [...requiredDomains].every(
    (domain) => modules.find((module) => module.domain === domain)?.status === EBusinessReportModuleStatus.AVAILABLE,
  )
  const availableCount = modules.filter((module) => module.status === EBusinessReportModuleStatus.AVAILABLE).length
  const minimumAvailable = "minimumAvailable" in definition ? definition.minimumAvailable : 0
  const complete = requiredComplete && availableCount >= minimumAvailable

  return {
    schemaVersion: parsed.schemaVersion,
    reportKind: parsed.reportKind,
    definitionVersion: parsed.definitionVersion,
    companyId: parsed.companyId,
    period: parsed.period,
    generatedAt: parsed.generatedAt,
    selectedProductKeys,
    complete,
    modules,
    findings,
    coverageStatement:
      modules.length === 0
        ? "No modules are configured. This edition does not establish an all-clear."
        : `${availableCount}/${modules.length} configured modules are available for the selected scope.`,
  }
}
